/**
 * @file This file defines the site settings used.
 *
 * NOTE: Do not edit this file as it is automatically @generated by "generate_site_settings.py".
 */
import moment from 'moment';
import { Fraction } from '@codegrade/bigfraction';
/* prettier-ignore */
/** The default value for the frontend site settings. */
export const FRONTEND_SETTINGS_DEFAULTS = Object.freeze({
    ASSIGNMENT_MAX_POINTS_ENABLED: true,
    DOWNLOAD_EDITOR_FILES_ENABLED: true,
    GRADING_NOTIFICATIONS_ENABLED: true,
    HIDE_NO_DEADLINE_ENABLED: false,
    SHOW_PREMIUM_BADGES: true,
    INLINE_RUBRIC_VIEWER_ENABLED: false,
    REGISTER_ENABLED: false,
    ASSIGNMENT_DEFAULT_GRADING_SCALE: "points",
    ASSIGNMENT_DEFAULT_GRADING_SCALE_POINTS: new Fraction(10, 1),
    ASSIGNMENT_PERCENTAGE_DECIMALS: 0,
    ASSIGNMENT_POINT_DECIMALS: 2,
    ASSISTANT_MODELS: ["claude-3.7-sonnet-v1", "claude-sonnet-4.5", "claude-sonnet-4.5-thinking"],
    CODE_EDITOR_START_ON_ASSIGNMENT_DESCRIPTION: false,
    DEFAULT_SUBMISSION_PAGE_TAB: "smart",
    HIDE_CODE_EDITOR_OUTPUT_VIEWER_WITH_ONLY_QUIZ_STEPS: false,
    HIDE_CODE_EDITOR_FILETREE_CONTROLS_WITH_ONLY_QUIZ_STEPS: false,
    NEW_AUTO_TEST_UBUNTU_20_04_BASE_IMAGE_IDS: ["base-image-ubuntu-20.04-initial"],
    NEW_AUTO_TEST_UBUNTU_24_04_BASE_IMAGE_IDS: ["base-image-ubuntu-24.04-initial"],
    QUIZ_MINIMUM_QUESTIONS_FOR_DROPDOWN: 5,
    SIMPLE_SUBMISSION_NAVIGATE_TO_LATEST_EDITOR_SESSION: false,
    ACCESS_TOKEN_MODAL_WARNING_TIME: moment.duration(120.0, 'seconds'),
    ACCESS_TOKEN_TOAST_WARNING_TIME: moment.duration(300.0, 'seconds'),
    LOGIN_TOKEN_BEFORE_TIME: [moment.duration(172800.0, 'seconds'), moment.duration(1800.0, 'seconds')],
    STUDENT_PAYMENT_MAIN_OPTION: "stripe",
    LTI_LOCK_DATE_COPYING_ENABLED: true,
    BLACKBOARD_ZIP_UPLOAD_ENABLED: true,
    IS_PEARSON: false,
    EXAM_HEARTBEAT: false,
    NEW_LOGIN_AND_REGISTER: true,
    TIER_FREE_AI_ENABLED: false,
    TIER_FREE_LTI_ENABLED: false,
    TIER_FREE_ATV1_ENABLED: false,
    TIER_FREE_JUPYTER_ENABLED: false,
    TIER_FREE_QUIZ_ENABLED: false,
    TIER_FREE_EXTENDED_RUNTIME_ENABLED: false,
    TIER_FREE_UNLIMITED_INSTALL_STEPS_ENABLED: false,
    TIER_FREE_MAX_STUDENTS: 50,
    TIER_FREE_MAX_STAFF: 5,
    TIER_STARTER_AI_ENABLED: false,
    TIER_STARTER_LTI_ENABLED: false,
    TIER_STARTER_ATV1_ENABLED: true,
    TIER_STARTER_JUPYTER_ENABLED: false,
    TIER_STARTER_QUIZ_ENABLED: false,
    TIER_STARTER_EXTENDED_RUNTIME_ENABLED: true,
    TIER_STARTER_UNLIMITED_INSTALL_STEPS_ENABLED: true,
    TIER_STARTER_MAX_STUDENTS: -1,
    TIER_STARTER_MAX_STAFF: -1,
    TIER_ADVANCED_AI_ENABLED: false,
    TIER_ADVANCED_LTI_ENABLED: true,
    TIER_ADVANCED_ATV1_ENABLED: true,
    TIER_ADVANCED_JUPYTER_ENABLED: true,
    TIER_ADVANCED_QUIZ_ENABLED: true,
    TIER_ADVANCED_EXTENDED_RUNTIME_ENABLED: true,
    TIER_ADVANCED_UNLIMITED_INSTALL_STEPS_ENABLED: true,
    TIER_ADVANCED_MAX_STUDENTS: -1,
    TIER_ADVANCED_MAX_STAFF: -1,
});
/* prettier-ignore */
/** The default value for the backend site settings. */
export const BACKEND_SETTINGS_DEFAULTS = Object.freeze({
    MAX_FILE_SIZE: 52428800,
    MAX_LARGE_UPLOAD_SIZE: 134217728,
    MAX_NORMAL_UPLOAD_SIZE: 67108864,
    DEFAULT_COURSE_TA_ROLE: "Full TA",
    DEFAULT_COURSE_TEACHER_ROLE: "Full Teacher",
    DEFAULT_COURSE_TIER: "advanced",
    NEW_AUTO_TEST_DEFAULT_OS: "Ubuntu 24.04",
    SEND_REGISTRATION_EMAIL: false,
    JWT_ACCESS_TOKEN_EXPIRES: moment.duration(604800.0, 'seconds'),
    MIN_PASSWORD_SCORE: 3,
    POW_DIFFICULTY: 18,
    PAYMENTS_PROVIDER: "stripe",
    PER_COURSE_PAYMENT_ALLOWED: true,
    HUBSPOT_SYNCING_ENABLED: true,
    LTI_1P3_NONCE_AND_STATE_VALIDATION_ENABLED: true,
    LTI_1P3_SYSTEM_ROLE_FROM_CONTEXT_ROLE: false,
    LTI_LAUNCH_DATA_LOGGING: false,
    LTI_ROLE_SWITCHING: false,
    NAME_AND_EMAIL_FROM_NRPS_ONLY: false,
    SSO_USERNAME_DECOLLISION_ENABLED: true,
});
/** The default value for all site settings. */
export const SITE_SETTINGS_DEFAULTS = Object.freeze({
    ...FRONTEND_SETTINGS_DEFAULTS,
    ...BACKEND_SETTINGS_DEFAULTS,
});
/* prettier-ignore */
/** All available site settings with documentation. */
export const ALL_SITE_SETTINGS = Object.freeze([
    Object.freeze({
        name: 'Features',
        settings: Object.freeze([
            Object.freeze({
                name: 'ASSIGNMENT_MAX_POINTS_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "Max Points" field within the assignment general settings is enabled. If disabled, teachers will not be able to award extra points for assignments.  Note: teachers requesting this feature should be aware that this does not change the grading scale; grades are always "out of 10". So if the max grade is configured to a value less than 10 students will never be able to achieve full marks for that assignment. The main use case for this feature is to be able to assign bonus points, not change the grading scale. ',
            }),
            Object.freeze({
                name: 'DOWNLOAD_EDITOR_FILES_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Allows users to download files from those listed in the file tree within the code editor. This includes quizzes, Jupyter Notebooks, images, code and other files which can be viewed as text. ',
            }),
            Object.freeze({
                name: 'GRADING_NOTIFICATIONS_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether teachers have access to the assignment manager - notifications panel, which gives control over when to send notifications to graders to finish their job, and also allows teachers to provide email(s) to notify when all graders are finished. ',
            }),
            Object.freeze({
                name: 'HIDE_NO_DEADLINE_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Should we hide the "No deadline" message if no deadline or lock date is set. ',
            }),
            Object.freeze({
                name: 'SHOW_PREMIUM_BADGES',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether to show premium badges and upsell modals in the UI. When disabled, steps and categories that would normally be shown as premium-gated are hidden entirely. Useful for paid tenants where premium indicators create confusion. ',
            }),
            Object.freeze({
                name: 'INLINE_RUBRIC_VIEWER_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the submission rubric viewer uses inline when the tab view does not fit. On the submission page we show the rubric viewer that will allow the teacher to fill in the rubric and the student to view their score in the different categories. With this feature enabled, we calculate whether all of the categories fit on a single line, and otherwise use a dropdown with previous and next buttons to select categories. ',
            }),
            Object.freeze({
                name: 'REGISTER_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Should it be possible to register on the website. This makes it possible for any body to register an account on the website. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'SystemLimits',
        settings: Object.freeze([
            Object.freeze({
                name: 'MAX_FILE_SIZE',
                typ: 'number',
                list: false,
                format: 'filesize',
                supportEditable: true,
                doc: "The maximum size of a single file uploaded by normal users. This limit is really here to prevent users from uploading extremely large files which can't really be downloaded/shown anyway. ",
            }),
            Object.freeze({
                name: 'MAX_LARGE_UPLOAD_SIZE',
                typ: 'number',
                list: false,
                format: 'filesize',
                supportEditable: true,
                doc: 'The maximum size of uploaded files that are mostly uploaded by "trusted" users. Examples of these kind of files include AutoTest fixtures and plagiarism base code. ',
            }),
            Object.freeze({
                name: 'MAX_NORMAL_UPLOAD_SIZE',
                typ: 'number',
                list: false,
                format: 'filesize',
                supportEditable: true,
                doc: 'The maximum total size of uploaded files that are uploaded by normal users. This is also the maximum total size of submissions. Increasing this size might cause a hosting costs to increase. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Configuration',
        settings: Object.freeze([
            Object.freeze({
                name: 'ASSIGNMENT_DEFAULT_GRADING_SCALE',
                typ: 'string-union',
                list: false,
                format: ['points', 'percentage'],
                supportEditable: true,
                doc: 'The default value for the grading scale of new assignments. ',
            }),
            Object.freeze({
                name: 'ASSIGNMENT_DEFAULT_GRADING_SCALE_POINTS',
                typ: 'Fraction',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The default points grading scale points of new assignments. ',
            }),
            Object.freeze({
                name: 'ASSIGNMENT_PERCENTAGE_DECIMALS',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Number of decimals for percentage-based grades in assignments, this also determines which decimal position the grade is rounded to. ',
            }),
            Object.freeze({
                name: 'ASSIGNMENT_POINT_DECIMALS',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Number of decimals for point-based grades in assignments, this also determines which decimal position the grade is rounded to. ',
            }),
            Object.freeze({
                name: 'ASSISTANT_MODELS',
                typ: 'string',
                list: true,
                format: '',
                supportEditable: false,
                doc: 'Which LLM models are allowed to create a new assistant with. Defines the entire list, which means it can also be used to deprecate models by removing them. Beware that adding something to this list does not automatically add support for in the system to actually use the LLM through bedrock. For that we require to set up our deploy config to allow it. Note: The ground truth for which models are actually set up is defined in our app configuration, this simply can be used to disable any model that is configured to no longer allow it. Similarly, adding an unknown model in this list will not suddenly enable it for use. ',
            }),
            Object.freeze({
                name: 'CODE_EDITOR_START_ON_ASSIGNMENT_DESCRIPTION',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether to open the assignment description as the first file in the Code Editor, if the assignment has one, or the first file in the file tree. ',
            }),
            Object.freeze({
                name: 'DEFAULT_COURSE_TA_ROLE',
                typ: 'string-union',
                list: false,
                format: ['Full TA', 'Non-Editing TA'],
                supportEditable: false,
                doc: 'The TA role to be used for TAs in new courses. Existing courses will not be affected. ',
            }),
            Object.freeze({
                name: 'DEFAULT_COURSE_TEACHER_ROLE',
                typ: 'string-union',
                list: false,
                format: ['Full Teacher', 'Non-Editing Teacher'],
                supportEditable: false,
                doc: 'The teacher role to be used for teachers in new courses. Existing courses will not be affected. ',
            }),
            Object.freeze({
                name: 'DEFAULT_COURSE_TIER',
                typ: 'string-union',
                list: false,
                format: ['free', 'starter', 'advanced'],
                supportEditable: true,
                doc: 'The default tier to select for new courses created in this tenant. ',
            }),
            Object.freeze({
                name: 'DEFAULT_SUBMISSION_PAGE_TAB',
                typ: 'string-union',
                list: false,
                format: ['smart', 'code', 'feedback-overview', 'auto-test', 'peer-feedback'],
                supportEditable: true,
                doc: 'The default tab to select when going to a submission. ',
            }),
            Object.freeze({
                name: 'HIDE_CODE_EDITOR_OUTPUT_VIEWER_WITH_ONLY_QUIZ_STEPS',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether we will hide all of the steps in the code editor sidebar output viewer if we have in a quiz-only atv2 config. ',
            }),
            Object.freeze({
                name: 'HIDE_CODE_EDITOR_FILETREE_CONTROLS_WITH_ONLY_QUIZ_STEPS',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether we will hide the buttons to add a file and a directory in the code editor, if we have a quiz-only atv2 config. ',
            }),
            Object.freeze({
                name: 'NEW_AUTO_TEST_DEFAULT_OS',
                typ: 'string-union',
                list: false,
                format: ['Ubuntu 20.04', 'Ubuntu 24.04'],
                supportEditable: false,
                doc: 'The default OS that should be used for new ATv2 configurations. ',
            }),
            Object.freeze({
                name: 'NEW_AUTO_TEST_UBUNTU_20_04_BASE_IMAGE_IDS',
                typ: 'string',
                list: true,
                format: '',
                supportEditable: false,
                doc: 'The base image that should be used for Ubuntu 20.04. ',
            }),
            Object.freeze({
                name: 'NEW_AUTO_TEST_UBUNTU_24_04_BASE_IMAGE_IDS',
                typ: 'string',
                list: true,
                format: '',
                supportEditable: false,
                doc: 'The base image that should be used for Ubuntu 24.04. ',
            }),
            Object.freeze({
                name: 'QUIZ_MINIMUM_QUESTIONS_FOR_DROPDOWN',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'The minimum number of questions a quiz needs in order to display a dropdown of all questions. ',
            }),
            Object.freeze({
                name: 'SEND_REGISTRATION_EMAIL',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether to send a registration email to new users upon registration. Note: this includes all registrations, also through LTI or SSO. ',
            }),
            Object.freeze({
                name: 'SIMPLE_SUBMISSION_NAVIGATE_TO_LATEST_EDITOR_SESSION',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether we update the simple submission mode navigation to check if we have an editor session which is later than the submission when we launch the assignment. This feature is only available when the simple submission mode is enabled. Be mindful of turning this on when the tenant uses deadlines. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Security',
        settings: Object.freeze([
            Object.freeze({
                name: 'ACCESS_TOKEN_MODAL_WARNING_TIME',
                typ: 'moment.Duration',
                list: false,
                format: 'timedelta',
                supportEditable: false,
                doc: "The amount of time before an access token expires when we will show a modal with a warning that the user's session is about to expire. ",
            }),
            Object.freeze({
                name: 'ACCESS_TOKEN_TOAST_WARNING_TIME',
                typ: 'moment.Duration',
                list: false,
                format: 'timedelta',
                supportEditable: false,
                doc: 'The amount of time before an access token expires when we will show a toast message warning the user that their session is about to expire. ',
            }),
            Object.freeze({
                name: 'JWT_ACCESS_TOKEN_EXPIRES',
                typ: 'moment.Duration',
                list: false,
                format: 'timedelta',
                supportEditable: false,
                doc: 'The time a login session is valid. After this amount of time a user will always need to re-authenticate. ',
            }),
            Object.freeze({
                name: 'LOGIN_TOKEN_BEFORE_TIME',
                typ: 'moment.Duration',
                list: true,
                format: 'timedelta',
                supportEditable: true,
                doc: 'This determines how long before the exam we will send the login emails to the students (only when enabled of course). ',
            }),
            Object.freeze({
                name: 'MIN_PASSWORD_SCORE',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'The minimum strength passwords by users should have. The higher this value the stronger the password should be. When increasing the strength all users with too weak passwords will be shown a warning on the next login. ',
            }),
            Object.freeze({
                name: 'POW_DIFFICULTY',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'The number of leading zero bits required in the SHA-256 hash for proof-of-work challenges. Higher values make challenges harder (each +1 roughly doubles solve time). The default of 18 is a balance between bot deterrence and user experience. Setting this on a tenant level does nothing. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Payment',
        settings: Object.freeze([
            Object.freeze({
                name: 'PAYMENTS_PROVIDER',
                typ: 'string-union',
                list: false,
                format: ['stripe', 'paddle'],
                supportEditable: true,
                doc: 'The payment provider to use for student payments. This should usually be configured at the tenant level, and will make new prices created in that tenant use this provider. Any existing prices and transactions will not be affected. ',
            }),
            Object.freeze({
                name: 'PER_COURSE_PAYMENT_ALLOWED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Controls whether users are also allowed to pay for courses individually. Tenant-wide access passes can always be used if available. If this setting is disabled, purchasing an access pass is the only way to pay for course access. If there are no tenant passes this setting has no influence. ',
            }),
            Object.freeze({
                name: 'STUDENT_PAYMENT_MAIN_OPTION',
                typ: 'string-union',
                list: false,
                format: ['stripe', 'coupon'],
                supportEditable: true,
                doc: 'Controls which option students will see by default when they have not yet paid for a course.  - `stripe`: pay by credit card (Stripe or Paddle). - `coupon`: redeem a coupon to get access to the course. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Hubspot',
        settings: Object.freeze([
            Object.freeze({
                name: 'HUBSPOT_SYNCING_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Should we sync teachers to Hubspot. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'LTI',
        settings: Object.freeze([
            Object.freeze({
                name: 'LTI_1P3_NONCE_AND_STATE_VALIDATION_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether LTI 1.3 launches using cookies (and sessions) should check for correct nonce and state. When disabling this feature flag, please be mindful. These validations protect us from certain attacks. If unsure, consult with the Security Officer before disabling. ',
            }),
            Object.freeze({
                name: 'LTI_1P3_SYSTEM_ROLE_FROM_CONTEXT_ROLE',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Also look at context roles when determining the system role for a new user in LMSes that have an ``extra_roles_mapping`` defined in their ``lms_capabilities``. ',
            }),
            Object.freeze({
                name: 'LTI_LAUNCH_DATA_LOGGING',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: "Enable logging of LTI launch data. NEVER ENABLE THIS SITE-WIDE, only for a single tenant, and disable this feature after you've gotten the data you need. ",
            }),
            Object.freeze({
                name: 'LTI_LOCK_DATE_COPYING_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Should the lock date be copied from the LMS through LTI, or should we allow the user to set it in CodeGrade. ',
            }),
            Object.freeze({
                name: 'LTI_ROLE_SWITCHING',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'This enables whether we change the role of a user in a course when they launch with a different role than their current one. ',
            }),
            Object.freeze({
                name: 'NAME_AND_EMAIL_FROM_NRPS_ONLY',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Do not store names and emails of users, but always retrieve them through NRPS. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'SSO',
        settings: Object.freeze([
            Object.freeze({
                name: 'SSO_USERNAME_DECOLLISION_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether a new user should be created on an SSO login of a user whose username is already taken by another user. The new user will get a ` (X)` suffixed to their username to prevent the username collision, where `X` is a number. When this is disabled the SSO user logging in will be linked to the user with the same username, if one already exists. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Custom',
        settings: Object.freeze([
            Object.freeze({
                name: 'BLACKBOARD_ZIP_UPLOAD_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'If enabled teachers are allowed to bulk upload submissions (and create users) using a zip file in a format created by Blackboard. ',
            }),
            Object.freeze({
                name: 'IS_PEARSON',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'Whether we are running Codegrade for Pearson specifically. This will: - Enable Pearson template assignments. - Hide grades from students. Rubrics results are still shown however. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'ReleaseFlags',
        settings: Object.freeze([
            Object.freeze({
                name: 'EXAM_HEARTBEAT',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'When enabled, isolated exam sessions use a heartbeat mechanism to detect laptop suspension. Sessions expire after 5 minutes without a heartbeat, terminating the exam session. Requires the per- restriction ``heartbeat_enforced`` toggle to also be enabled. ',
            }),
            Object.freeze({
                name: 'NEW_LOGIN_AND_REGISTER',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: false,
                doc: 'We introduced new login and register flow along with the Freemium model. When set to true, this setting will switch login and register screens to a new page design, making the login via email the first and preferred. ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Free Tier Add-ons',
        settings: Object.freeze([
            Object.freeze({
                name: 'TIER_FREE_AI_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to AI-backed features such as the assistant and the ATv2 prompt engineering step. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_LTI_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to LTI integration, allowing the course to be launched from an LMS. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_ATV1_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to AutoTest v1, the legacy automatic grading system. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_JUPYTER_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to ATv2 Jupyter Notebook steps. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_QUIZ_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to ATv2 coding quiz steps. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_EXTENDED_RUNTIME_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to extended runtime for grading tests. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_UNLIMITED_INSTALL_STEPS_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "free" tier gives access to unlimited setup install blocks. ',
            }),
            Object.freeze({
                name: 'TIER_FREE_MAX_STUDENTS',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The "free" tier\'s limit for the maximum number of students allowed in a course (negative = unlimited). ',
            }),
            Object.freeze({
                name: 'TIER_FREE_MAX_STAFF',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The "free" tier\'s limit for the maximum number of staff allowed in a course (negative = unlimited). ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Starter Tier Add-ons',
        settings: Object.freeze([
            Object.freeze({
                name: 'TIER_STARTER_AI_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to AI-backed features such as the assistant and the ATv2 prompt engineering step. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_LTI_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to LTI integration, allowing the course to be launched from an LMS. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_ATV1_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to AutoTest v1, the legacy automatic grading system. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_JUPYTER_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to ATv2 Jupyter Notebook steps. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_QUIZ_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to ATv2 coding quiz steps. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_EXTENDED_RUNTIME_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to extended runtime for grading tests. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_UNLIMITED_INSTALL_STEPS_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "starter" tier gives access to unlimited setup install blocks. ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_MAX_STUDENTS',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The "starter" tier\'s limit for the maximum number of students allowed in a course (negative = unlimited). ',
            }),
            Object.freeze({
                name: 'TIER_STARTER_MAX_STAFF',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The "starter" tier\'s limit for the maximum number of staff allowed in a course (negative = unlimited). ',
            }),
        ]),
    }),
    Object.freeze({
        name: 'Advanced Tier Add-ons',
        settings: Object.freeze([
            Object.freeze({
                name: 'TIER_ADVANCED_AI_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to AI-backed features such as the assistant and the ATv2 prompt engineering step. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_LTI_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to LTI integration, allowing the course to be launched from an LMS. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_ATV1_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to AutoTest v1, the legacy automatic grading system. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_JUPYTER_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to ATv2 Jupyter Notebook steps. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_QUIZ_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to ATv2 coding quiz steps. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_EXTENDED_RUNTIME_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to extended runtime for grading tests. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_UNLIMITED_INSTALL_STEPS_ENABLED',
                typ: 'boolean',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'Whether the "advanced" tier gives access to unlimited setup install blocks. ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_MAX_STUDENTS',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The "advanced" tier\'s limit for the maximum number of students allowed in a course (negative = unlimited). ',
            }),
            Object.freeze({
                name: 'TIER_ADVANCED_MAX_STAFF',
                typ: 'number',
                list: false,
                format: '',
                supportEditable: true,
                doc: 'The "advanced" tier\'s limit for the maximum number of staff allowed in a course (negative = unlimited). ',
            }),
        ]),
    }),
]);
/** Resolves a tier add-on to the corresponding site setting for a given course. */
class Addon {
    tag;
    /** @param tag - The add-on identifier. */
    constructor(tag) {
        this.tag = tag;
        Object.freeze(this);
    }
    /**
     * Get the site setting key for this add-on based on the course's tier.
     *
     * @param course - The course to resolve the setting for.
     * @returns The site setting key for this add-on and the course's tier.
     */
    getSetting(course) {
        const tier = course.tier.toUpperCase();
        return `TIER_${tier}_${this.tag}`;
    }
}
/* prettier-ignore */
/** All tier add-ons. */
export const ALL_TIER_ADDONS = Object.freeze({
    AI_ENABLED: new Addon('AI_ENABLED'),
    LTI_ENABLED: new Addon('LTI_ENABLED'),
    ATV1_ENABLED: new Addon('ATV1_ENABLED'),
    JUPYTER_ENABLED: new Addon('JUPYTER_ENABLED'),
    QUIZ_ENABLED: new Addon('QUIZ_ENABLED'),
    EXTENDED_RUNTIME_ENABLED: new Addon('EXTENDED_RUNTIME_ENABLED'),
    UNLIMITED_INSTALL_STEPS_ENABLED: new Addon('UNLIMITED_INSTALL_STEPS_ENABLED'),
    MAX_STUDENTS: new Addon('MAX_STUDENTS'),
    MAX_STAFF: new Addon('MAX_STAFF'),
});
/**
 * Resolve all course tier addons from a course and its site settings.
 *
 * @param course The course to resolve addons for.
 * @param settings The site settings for the course's tenant.
 * @returns The resolved tier addon flags.
 */
export function resolveCourseTierAddons(course, settings) {
    // prettier-ignore
    return {
        aiEnabled: settings[ALL_TIER_ADDONS.AI_ENABLED.getSetting(course)],
        ltiEnabled: settings[ALL_TIER_ADDONS.LTI_ENABLED.getSetting(course)],
        atv1Enabled: settings[ALL_TIER_ADDONS.ATV1_ENABLED.getSetting(course)],
        jupyterEnabled: settings[ALL_TIER_ADDONS.JUPYTER_ENABLED.getSetting(course)],
        quizEnabled: settings[ALL_TIER_ADDONS.QUIZ_ENABLED.getSetting(course)],
        extendedRuntimeEnabled: settings[ALL_TIER_ADDONS.EXTENDED_RUNTIME_ENABLED.getSetting(course)],
        unlimitedInstallStepsEnabled: settings[ALL_TIER_ADDONS.UNLIMITED_INSTALL_STEPS_ENABLED.getSetting(course)],
        maxStudents: settings[ALL_TIER_ADDONS.MAX_STUDENTS.getSetting(course)],
        maxStaff: settings[ALL_TIER_ADDONS.MAX_STAFF.getSetting(course)],
    };
}
